import React, {
  Component,
  createRef,
} from 'react';
import {
  inject,
  observer,
} from 'mobx-react';
import './Footer.css';
import { TEMP_UNITS } from '../../stores/weather-store';
import Time from '../Time/Time';
import { withTranslation } from 'react-i18next';
import Theme from '../Theme/Theme';
import Timeline from '../Timeline/Timeline';
import UiSize from '../UiSize/UiSize';
import MaintenanceMode from '../MaintenanceMode/MaintenanceMode';
import { SwitchCustom } from '../Swtitch/Switch';
import {
  setTimerSpeedModifier,
  getTimerSpeedModifier,
} from '../../utils/timer-manager/timer-manager';
import { storeEmitter } from '../../stores/batch/initializeEmitter';
import logger from '../../utils/logger';

@inject('batchStore', 'scheduleStore', 'devicesStore', 'materialStore', 'mixStore', 'dischargeStore', 'ticketStore', 'websocketStore', 'weatherStore', 'settingsStore', 'newAuthStore', 'pacStore', 'connectionStore', 'locationsStore')
@observer
class Footer extends Component {
  footerRef = createRef();

  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      count: 0,
      timerSpeed: getTimerSpeedModifier(),
    };
  }

  componentDidMount() {
    const { batchStore, pacStore, scheduleStore, devicesStore, settingsStore, materialStore, mixStore, dischargeStore, ticketStore } = this.props;

    if (!batchStore.observeStartTime) {
      batchStore.observeBatchData();
      scheduleStore.startWebSockets();
      devicesStore.startWebSockets();
      settingsStore.startWebSockets();
      materialStore.startWebSockets();
      mixStore.startWebSockets();
      dischargeStore.startWebSockets();
      ticketStore.startWebSockets();
      batchStore.observeStartTime = Date.now();
    }

    pacStore.connect(() => {
      batchStore.getPacVersion();
    });

    this.checkConnection();
    this.getWeather();

    this.loadingTimer = setTimeout(() => {
      this.setState({
        loading: false,
      });
    }, 5000);
  }

  componentWillUnmount() {
    clearTimeout(this.timer);
    clearTimeout(this.weatherTimer);
    clearTimeout(this.loadingTimer);
  }

  allowScrollingForObserver() {
    const { batchStore } = this.props;
    const { canEmit } = batchStore;
    const cl = 'f-scrolling';

    if (this.isBatchPage && !canEmit) {
      this.footerRef.current.classList.add(cl);

      clearTimeout(this.scrollTimer);

      this.scrollTimer = setTimeout(()=>{
        this.footerRef.current.classList.remove(cl);
      }, 100);
    }
  }

  checkConnection() {
    const { batchStore, websocketStore, connectionStore } = this.props;
    const { count } = this.state;

    if (!websocketStore.connected && connectionStore.isOnline && count < 7) {
      this.timer = setTimeout(() => {
        this.setState({ count: count+1 });
        this.checkConnection();
      }, 1000);

      return;
    }

    clearTimeout(this.timer);
    const waitTimeForOtherEmitter = 5000;
    logger.log(`waiting ${waitTimeForOtherEmitter/1000} sec for other emitter`);
    // waiting for other emitter
    this.timer = setTimeout(() => {
      if (batchStore.canEmit) {
        batchStore.sendPacInitialCommands();
        storeEmitter(batchStore.emitter);
      }

    }, waitTimeForOtherEmitter);
  }

  getWeather = () => {
    const { weatherStore, settingsStore } = this.props;

    clearTimeout(this.weatherTimer);

    if (settingsStore.configuration) {
      weatherStore.getCurrentWeather();
    }

    this.weatherTimer = setTimeout(() => {
      this.getWeather();
    }, 1000 * 600);
  };

  handleTimerSpeedChange = (speed) => {
    setTimerSpeedModifier(speed);
    this.setState({ timerSpeed: speed });
  };

  get isBatchPage() {
    const { pathname } = this.props.location;
    return pathname === '/' || String(pathname).includes('index');
  }

  render () {
    const { newAuthStore, batchStore, websocketStore, weatherStore, settingsStore, connectionStore, locationsStore, pacStore, t } = this.props;
    const { canEmit, pacConnected, pacVersion, replayMode, loadHistory, remoteControl, remoteUser, isPacInitialCommandSent, proxyVersion, proxyIsOnline } = batchStore;
    const { loading, timerSpeed } = this.state;
    const { user } = newAuthStore;
    const { currentPlant, currentLocation } = locationsStore;
    let className = canEmit ? 'emitter' :  this.isBatchPage ? 'observer' : '';
    className += websocketStore.connected ? ' websocket-connected' : '';
    className += websocketStore.isTrulyConnected ? ' websocket-truly-connected' : '';
    className += pacConnected && pacVersion && batchStore.isPacUidValid(pacVersion.uid) ? ' pac-connected' : '';
    className += pacStore.isWSOpen() ? ' proxy-connected' : '';
    className += proxyIsOnline ? ' proxy-is-online' : '';
    className += connectionStore.isOnline && !websocketStore.connected && loading ? ' loading' : '';
    className += newAuthStore.tokenExpired ? ' token-expired' : '';
    className += remoteControl ? ' remote-control' : '';

    return (
      <footer id={'footer'} className={`${className}`} ref={this.footerRef} onWheel={() => this.allowScrollingForObserver()}>
        <div className="footer-item">
          <i id="internet-connection" data-e2e-id="internet-connection" className="fa fa-wifi" title="Internet" onClick={() => connectionStore.toggleOffline()}/>
          <i id="websocket-connection" data-e2e-id="websocket-connection" className="fa fa-eye" title="Websocket"/>
          <i id="proxy-connection" data-e2e-id="proxy-connection" className="fa fa-signal" title="Proxy"/>
          {isPacInitialCommandSent && pacConnected && !proxyIsOnline && (
            <span id="proxy-status-version" data-e2e-id="proxy-status-version">{t('Offline Proxy')} {proxyVersion}</span>
          )}
          <span id="user-info" data-e2e-id="user-info" title={`${user.name} ${user.language}`}><i className="fa fa-user"/> {user.login}</span>
          {remoteUser?.login && (
            <span id="remote-connection-status" data-e2e-id="remote-connection-status" className="active-user" title={`Remote User: ${remoteUser.username} ${remoteUser.userTimeZone}`}>
              {t('Remote Control by')}: <i className="fa fa-user" /> {remoteUser.login}
            </span>
          )}
          <Time />
          <span id="plant-info" data-e2e-id="plant-info" title={`#${locationsStore.plantId}`}>{t('Plant')}: {currentPlant.description}</span>
          <span id="location-info" data-e2e-id="location-info" title={`#${locationsStore.locationId}`}>{t('Location')}: {currentLocation.name}</span>
          <span data-e2e-id="lbl-sysdyne-info">Sysdyne Technologies Model: BatchGo</span>
          {settingsStore.plantSettings?.ntep && (
            <span id="ntep-info" data-e2e-id="ntep-info">NTEP CC: {settingsStore.plantSettings.ntep}</span>
          )}
          <span id="app-version" data-e2e-id="app-version" title={settingsStore.apiVersion}>V{process.env.REACT_APP_VERSION}</span>

          {weatherStore.temperature && (
            <span id="weather-info" data-e2e-id="weather-info" onClick={this.getWeather}>
              <i className="fa fa-cloud"
                title={`${weatherStore.city}, ${weatherStore.country}`}
              />
              {weatherStore.temperature}&#176;
              {weatherStore.tempUnits === TEMP_UNITS.IMPERIAL ? 'F' : 'C'}
            </span>
          )}

          <Theme id={locationsStore.plantId}/>
          <UiSize id={locationsStore.plantId}/>
          <MaintenanceMode/>

          {!!loadHistory.length &&
            <Timeline/>
          }

          {!canEmit && !batchStore.canSendRemoteInstruction() &&
            <span id="observer-mode-info" data-e2e-id="observer-mode-info"> {t('Observer Mode')}</span>
          }

          {batchStore.canSendRemoteInstruction() &&
            <span id="remote-control-info" data-e2e-id="remote-control-info"> {t('Remote Control')}</span>
          }

          {batchStore.canToggleRemoteControl() && (
            <SwitchCustom
              className="remote-control-switch"
              checked={remoteControl}
              title="Remote Control"
              size="medium"
              color="primary"
              onChange={() => batchStore.toggleRemoteControl()} />
          )}

          {!canEmit && pacConnected && !replayMode && (
            <span className="btn-wrapper">
              <button id="take-control-button" data-e2e-id="take-control-button" className="btn btn-primary btn-batch-controller"
                onClick={() => batchStore.takeBatchControl()}>
                {t('Take control')}
              </button>
            </span>
          )}

          {process.env.NODE_ENV === 'development' && (
            <span className="btn-wrapper timer-speed-controls">
              <span className="timer-speed-label">Simulation Speed:</span>
              <button
                className={`timer-speed-btn ${timerSpeed === 1 ? 'active' : ''}`}
                onClick={() => this.handleTimerSpeedChange(1)}
                title="Normal speed (1x)">
                1x
              </button>
              <button
                className={`timer-speed-btn ${timerSpeed === 2 ? 'active' : ''}`}
                onClick={() => this.handleTimerSpeedChange(2)}
                title="2x faster">
                2x
              </button>
              <button
                className={`timer-speed-btn ${timerSpeed === 5 ? 'active' : ''}`}
                onClick={() => this.handleTimerSpeedChange(5)}
                title="5x faster">
                5x
              </button>
              <button
                className={`timer-speed-btn ${timerSpeed === 15 ? 'active' : ''}`}
                onClick={() => this.handleTimerSpeedChange(15)}
                title="15x faster">
                15x
              </button>
            </span>
          )}

          {process.env.NODE_ENV === 'development' && process.env.REACT_APP_LOAD_FROM_CACHE === 'true' && (
            <span className="btn-wrapper">
              <button
                className="btn btn-primary"
                onClick={() => newAuthStore.purgeCacheDB()}
                title="Clear IndexDB cache (dev cache + static files)">
                Purge Cache
              </button>
            </span>
          )}
        </div>
      </footer>
    );
  }
}

export default withTranslation()(Footer);
